import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Fade,
  Chip,
  Button,
  Menu,
  MenuItem,
  ListItemAvatar,
  useTheme,
  alpha,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Autorenew as AutorenewIcon,
  AddCard as AddCardIcon,
} from '@mui/icons-material';
import { AccountOption } from './AccountSwitchDialog';
import { useNavigate } from 'react-router-dom';
import { creditApi, OrganizationCredits } from '../../utils/creditApi';
import { extraCreditsApi } from '../../utils/extraCreditsApi';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useWhiteLabelHiding } from '../../hooks/useWhiteLabelHiding';
import AddCreditsModal from '../AddCreditsModal';
import { useOrganizationSearch } from '../../hooks/useOrganizationSearch';
import { useTranslation } from 'react-i18next';

interface CollapsibleSidebarProps {
  menuItems: Array<{
    text: string;
    icon: React.ReactNode;
    path: string;
    category?: string;
  }>;
  selectedAccount: AccountOption | null;
  accounts: AccountOption[];
  onSelectAccount: (accountId: string) => void;
  onNavigateToNormalDashboard: () => void;
  onLogout: () => void;
  onMenuItemClick: (path: string) => void;
  isActiveRoute: (path: string, exact?: boolean) => boolean;
  isMobile?: boolean;
  onClose?: () => void;
  showAccountSwitcher?: boolean;
  planInactiveAlertOpen?: boolean;
}

const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  menuItems,
  selectedAccount,
  accounts,
  onSelectAccount,
  onNavigateToNormalDashboard,
  onLogout,
  onMenuItemClick,
  isActiveRoute,
  isMobile = false,
  onClose,
  showAccountSwitcher = false,
  planInactiveAlertOpen = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, currentOrganization } = useAuth();
  const { canShowAlert } = usePermissions();
  const { shouldHidePaymentElements } = useWhiteLabelHiding();

  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [extraCredits, setExtraCredits] = useState(0);
  const [extraCreditsUsed, setExtraCreditsUsed] = useState(0);
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [addCreditsModalOpen, setAddCreditsModalOpen] = useState(false);
  const [organizationCredits, setOrganizationCredits] = useState<OrganizationCredits | null>(null);

  // Hook para busca de organizações com debounce
  const {
    searchTerm,
    setSearchTerm,
    organizations: searchResults,
    recentOrganizations,
    isLoading: isSearchLoading,
    isSearching,
    addToRecent,
    clearSearch,
    hasSearchTerm
  } = useOrganizationSearch(selectedAccount?.id);

  // const isExpanded = hovered && !isMobile;
  const isExpanded = true;
  const sidebarWidth = isExpanded ? 280 : 72;

  // Função helper para buscar e atualizar dados de créditos
  const loadAndUpdateCredits = async () => {
    try {
      const [creditsData, extraCreditsData] = await Promise.all([
        creditApi.getOrganizationCredits(),
        extraCreditsApi.getOrganizationExtraCredits()
      ]);
      
      setTotalCredits(creditsData.credit);
      setCreditsUsed(creditsData.usedThisMonth);
      setOrganizationCredits(creditsData);
      
      // Calcular créditos extras ativos (apenas IsActive = true)
      const activeExtras = extraCreditsData.extraCreditsHistory.filter(ec => ec.active);
      const activeExtraCredits = activeExtras.reduce((sum, ec) => sum + ec.amount, 0);
      const activeExtraCreditsUsed = activeExtras.reduce((sum, ec) => sum + (ec.used || 0), 0);
      
      setExtraCredits(activeExtraCredits);
      setExtraCreditsUsed(activeExtraCreditsUsed);
    } catch (error) {
      console.error('Error loading credits data:', error);
      // Manter valores padrão em caso de erro
    }
  };

  // Carregar dados de créditos quando o componente for montado
  React.useEffect(() => {
    loadAndUpdateCredits();
  }, []);

  // Atualização automática a cada 30 segundos
  React.useEffect(() => {
    const interval = setInterval(() => {
      loadAndUpdateCredits();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, []);

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
    clearSearch(); // Clear search when opening
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
    clearSearch();
  };

  const handleAccountSelect = (accountId: string) => {
    // Encontrar a organização selecionada e adicionar aos recentes
    const selectedAccount = accounts.find(acc => acc.id === accountId);
    if (selectedAccount) {
      addToRecent({
        id: selectedAccount.id,
        name: selectedAccount.name,
        identifier: selectedAccount.id,
        active: true,
        currentPlan: undefined,
        endIn: undefined,
        subscriptionActive: true
      });
    }

    onSelectAccount(accountId);
    handleAccountMenuClose();
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Determinar quais contas mostrar (busca ou recentes)
  const displayAccounts = React.useMemo(() => {
    if (hasSearchTerm) {
      // Converter resultados da busca para o formato AccountOption
      return searchResults.map(org => ({
        id: org.id,
        name: org.name
      }));
    } else {
      // Mostrar contas recentes ou todas as contas se não houver recentes
      if (recentOrganizations.length > 0) {
        return recentOrganizations.map(org => ({
          id: org.id,
          name: org.name
        }));
      }
      return accounts;
    }
  }, [hasSearchTerm, searchResults, recentOrganizations, accounts]);

  const handleCreditsRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadAndUpdateCredits();
    } catch (error) {
      console.error('Error fetching credits:', error);
      // Manter valores atuais em caso de erro
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCreditsUpdate = async () => {
    try {
      const creditsData = await creditApi.getOrganizationCredits();
      setTotalCredits(creditsData.credit);
      setCreditsUsed(creditsData.usedThisMonth);
      setCreditsModalOpen(true);
    } catch (error) {
      console.error('Error fetching credits:', error);
      // Em caso de erro, não definir valores fallback hardcoded
      setCreditsModalOpen(true);
    }
  };

  const handleCreditsModalClose = () => {
    setCreditsModalOpen(false);
  };

  const handleNavigateToCredits = () => {
    navigate('/comprar-creditos');
    setCreditsModalOpen(false);
  };

  const handleNavigateToCreditHistory = () => {
    navigate('/admin/credit-history');
  };

  // Verificar se os créditos atingiram o limite e se não é WhiteLabel gerenciado
  const shouldShowAddCreditsButton = () => {
    if (!organizationCredits || shouldHidePaymentElements) return false;

    // Verificar se a organização não é gerenciada por WhiteLabel
    if (currentOrganization?.isWhiteLabelManaged === true) return false;

    // Verificar se os créditos estão no limite (90% ou mais usados)
    const usagePercentage = (organizationCredits.usedThisMonth / organizationCredits.totalAvailableCredits) * 100;
    return usagePercentage >= 90;
  };

  const handleAddCreditsSuccess = async () => {
    // Recarregar os créditos após a compra
    await loadAndUpdateCredits();
  };

  const sidebarStyles = {
    width: sidebarWidth,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    background: `#1a1a1a`,
    color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative' as const,
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `#1a1a1a`,
      pointerEvents: 'none',
      transition: 'all 0.15s ease',
    },
  };

  const headerStyles = {
    p: 2,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(248,250,252,0.2)' : 'rgba(255,255,255,0.15)'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: isExpanded ? 'space-between' : 'center',
    gap: 1,
    position: 'relative' as const,
    zIndex: 1,
  };

  const logoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    transition: 'all 0.3s ease',
    opacity: isExpanded ? 1 : 0,
    transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
  };


  return (
    <Box
      sx={sidebarStyles}
      onMouseEnter={() => { }}
      onMouseLeave={() => { }}
    >
      {/* Header */}
      <Box sx={headerStyles}>
        <Fade in={isExpanded} timeout={150}>
          <Box sx={logoStyles}>
            {/* <img
              src="/logo.png"
              alt="Logo"
              style={{
                height: 28,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            /> */}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #ecf0f1, #bdc3c7)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('admin.title')}
            </Typography>
          </Box>
        </Fade>

        {isMobile && onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
              backgroundColor: alpha('#ecf0f1', 0.1),
              '&:hover': {
                backgroundColor: alpha('#ecf0f1', 0.2),
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Account Switcher - Only for SuperAdmin */}
      {showAccountSwitcher && (
        <Box sx={{ p: 2, position: 'relative', zIndex: 1 }}>
          <Button
            fullWidth
            onClick={handleAccountMenuOpen}
            sx={{
              justifyContent: isExpanded ? 'flex-start' : 'center',
              p: isExpanded ? 1.5 : 1,
              borderRadius: 2,
              backgroundColor: isExpanded ? alpha('#ecf0f1', 0.1) : 'transparent',
              border: isExpanded ? `1px solid ${alpha('#ecf0f1', 0.2)}` : 'none',
              color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
              textTransform: 'none',
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              '& .MuiTouchRipple-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: isExpanded ? 'flex-start' : 'center',
              },
              '&:hover': {
                backgroundColor: isExpanded ? alpha('#ecf0f1', 0.2) : alpha('#ecf0f1', 0.05),
                borderColor: isExpanded ? alpha('#ecf0f1', 0.4) : 'transparent',
                transform: 'translateY(-1px)',
                boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: isExpanded ? 1.5 : 0,
                backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.8) : alpha('#3498db', 0.8),
                fontSize: '0.875rem',
              }}
            >
              <BusinessIcon fontSize="small" />
            </Avatar>
            <Fade in={isExpanded} timeout={150}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                opacity: isExpanded ? 1 : 0,
                transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
              }}>
                <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedAccount?.name || t('sidebar.selectAccount')}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    {t('sidebar.switchAccount')} {accounts.length > 1 && `(${accounts.length} ${t('sidebar.available')})`}
                  </Typography>
                </Box>
                <ExpandMoreIcon sx={{ fontSize: '1rem', opacity: 0.7 }} />
              </Box>
            </Fade>
          </Button>

          <Menu
            anchorEl={accountMenuAnchor}
            open={Boolean(accountMenuAnchor)}
            onClose={handleAccountMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 280,
                maxWidth: 400,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                border: `1px solid ${alpha('#ecf0f1', 0.1)}`,
              },
            }}
          >
            {/* Search Field */}
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('sidebar.searchOrganization')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />,
                  endAdornment: isSearchLoading ? (
                    <AutorenewIcon
                      sx={{
                        fontSize: '1rem',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    />
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
              {hasSearchTerm ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {isSearching ? t('sidebar.searching') : t('sidebar.searchResults', { count: searchResults.length })}
                </Typography>
              ) : recentOrganizations.length > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('sidebar.recentOrganizations', { count: recentOrganizations.length })}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('sidebar.availableOrganizations', { count: accounts.length })}
                </Typography>
              )}
            </Box>

            {/* Account List */}
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {displayAccounts.map((account) => (
                <MenuItem
                  key={account.id}
                  onClick={() => handleAccountSelect(account.id)}
                  selected={account.id === selectedAccount?.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.8) : alpha('#3498db', 0.8) }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={account.name}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: account.id === selectedAccount?.id ? 600 : 400
                    }}
                  />
                  {account.id === selectedAccount?.id && (
                    <Chip
                      label={t('sidebar.current')}
                      size="small"
                      color="primary"
                      sx={{ ml: 1, color: theme.palette.mode === 'dark' ? '#000' : undefined, fontSize: '0.7rem' }}
                    />
                  )}
                  {!hasSearchTerm && recentOrganizations.some(org => org.id === account.id) && account.id !== selectedAccount?.id && (
                    <Chip
                      label={t('sidebar.recent')}
                      size="small"
                      color="secondary"
                      sx={{ ml: 1, color: theme.palette.mode === 'dark' ? '#000' : undefined, fontSize: '0.7rem' }}
                    />
                  )}
                </MenuItem>
              ))}

              {displayAccounts.length === 0 && hasSearchTerm && !isSearching && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <SearchIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">
                    {t('sidebar.noOrganizationsFound')}
                  </Typography>
                  <Typography variant="caption">
                    {t('sidebar.tryAdjustingSearch')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Divider and New Account Button */}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddCardIcon />}
                onClick={() => {
                  handleAccountMenuClose();
                  onMenuItemClick('/admin/whitelabel/contas/create-with-user');
                }}
                sx={{
                  py: 1.5,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {t('sidebar.newAccount')}
              </Button>
            </Box>
          </Menu>
        </Box>
      )}

      {/* Navigation Menu */}
      <List sx={{
        flex: 1,
        p: 1,
        position: 'relative',
        zIndex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'transparent',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'transparent',
          borderRadius: '4px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: alpha('#ecf0f1', 0.4),
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        scrollbarColor: 'transparent transparent',
        '&:hover': {
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#ecf0f1', 0.2),
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          scrollbarColor: `${alpha('#ecf0f1', 0.2)} transparent`,
        },
      }}>
        {(() => {
          // Agrupar itens por categoria
          const groupedItems = menuItems.reduce((acc, item) => {
            const category = item.category || t('sidebar.categories.general');
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(item);
            return acc;
          }, {} as Record<string, typeof menuItems>);

          return Object.entries(groupedItems).map(([category, items], categoryIndex) => (
            <React.Fragment key={category}>
              {/* Subtítulo da categoria */}
              {isExpanded && (
                <Fade in={isExpanded} timeout={150}>
                  <Box sx={{ px: 2, py: 1, mt: categoryIndex > 0 ? 2 : 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha('#ecf0f1', 0.6),
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        opacity: isExpanded ? 1 : 0,
                        transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
                      }}
                    >
                      {category}
                    </Typography>
                    <Divider
                      sx={{
                        mt: 0.5,
                        borderColor: alpha('#ecf0f1', 0.1),
                        opacity: isExpanded ? 1 : 0,
                        transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
                      }}
                    />
                  </Box>
                </Fade>
              )}

              {/* Itens da categoria */}
              {items.map((item) => {
                const isActive = isActiveRoute(item.path);
                return (
                  <Tooltip
                    key={item.path}
                    title={!isExpanded ? item.text : ''}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      onClick={() => onMenuItemClick(item.path)}
                      selected={isActive}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                        minHeight: 48,
                        px: isExpanded ? 2 : 1.5,
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                        position: 'relative',
                        '& .MuiTouchRipple-root': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isExpanded ? 'flex-start' : 'center',
                        },
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                          minWidth: isExpanded ? 40 : 'auto',
                          justifyContent: 'center',
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.2) : alpha('#3498db', 0.2),
                          border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.3) : alpha('#3498db', 0.3)}`,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.3) : alpha('#3498db', 0.3),
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: '60%',
                            backgroundColor: theme.palette.mode === 'dark' ? '#65f0c5' : '#3498db',
                            borderRadius: '0 2px 2px 0',
                          },
                        },
                        '&:hover': {
                          backgroundColor: alpha('#ecf0f1', 0.1),
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <Fade in={isExpanded} timeout={150}>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            color: 'inherit',
                            fontSize: '0.875rem',
                            fontWeight: isActive ? 600 : 400,
                          }}
                          sx={{
                            opacity: isExpanded ? 1 : 0,
                            transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
                          }}
                        />
                      </Fade>
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ));
        })()}
      </List>

      {/* Credits Section */}
      {!shouldHidePaymentElements && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.15)', position: 'relative', zIndex: 1 }}>
          <Fade in={isExpanded} timeout={150}>
            <Box sx={{
              opacity: isExpanded ? 1 : 0,
              transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {t('sidebar.credits')}
                </Typography>
                <Tooltip title={t('sidebar.refreshCredits')} arrow>
                  <IconButton
                    size="small"
                    onClick={handleCreditsRefresh}
                    disabled={isRefreshing}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                      opacity: isRefreshing ? 0.7 : 1,
                      '&:hover': {
                        backgroundColor: alpha('#ecf0f1', 0.1),
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AutorenewIcon
                      sx={{
                        fontSize: '1rem',
                        animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Créditos Mensais */}
              <Box sx={{ mb: 1 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {t('sidebar.monthly')}: {Math.min(creditsUsed, totalCredits)}/{totalCredits}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      minWidth: 'fit-content'
                    }}
                  >
                    ({totalCredits > 0 ? Math.min((Math.min(creditsUsed, totalCredits) / totalCredits) * 100, 100).toFixed(0) : 0}%)
                  </Typography>
                </Box>

                {/* Barra de Progresso para Créditos Mensais */}
                <Box display="flex" alignItems="center" gap={1} mb={extraCredits > 0 ? 1 : 0}>
                  <LinearProgress
                    variant="determinate"
                    value={totalCredits > 0 ? Math.min((Math.min(creditsUsed, totalCredits) / totalCredits) * 100, 100) : 0}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha('#ecf0f1', 0.2),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: (() => {
                          if (totalCredits === 0) return '#27ae60';
                          const percentage = (Math.min(creditsUsed, totalCredits) / totalCredits) * 100;
                          if (percentage >= 100) return '#e74c3c';
                          if (percentage > 90) return '#f39c12';
                          return '#27ae60';
                        })(),
                        borderRadius: 3,
                      }
                    }}
                  />
                </Box>

                {/* Créditos Extras */}
                {(() => {
                  const extraTotal = extraCredits;
                  const extraUsed = extraCreditsUsed;
                  const extraPercentage = extraTotal > 0 ? Math.min((extraUsed / extraTotal) * 100, 100) : 0;
                  
                  return extraTotal > 0 && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4CAF50',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {t('sidebar.extras')}: {extraUsed.toLocaleString('pt-BR')}/{extraTotal.toLocaleString('pt-BR')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4CAF50',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            minWidth: 'fit-content'
                          }}
                        >
                          ({extraPercentage.toFixed(0)}%)
                        </Typography>
                      </Box>

                      {/* Barra de Progresso para Créditos Extras */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={extraPercentage}
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: alpha('#4CAF50', 0.2),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: (() => {
                                if (extraTotal === 0) return '#4CAF50';
                                if (extraPercentage >= 100) return '#e74c3c';
                                if (extraPercentage > 80) return '#f39c12';
                                return '#4CAF50';
                              })(),
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {planInactiveAlertOpen && user?.isPlanActive === false && canShowAlert('showPlanInactiveAlerts') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCreditsUpdate}
                    startIcon={<RefreshIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                      borderColor: alpha('#ecf0f1', 0.4),
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        borderColor: '#ecf0f1',
                        backgroundColor: alpha('#ecf0f1', 0.1),
                      }
                    }}
                  >
                    {t('common.refresh')}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleNavigateToCreditHistory}
                  startIcon={<HistoryIcon sx={{ fontSize: '1rem' }} />}
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
                    borderColor: alpha('#ecf0f1', 0.4),
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      borderColor: '#ecf0f1',
                      backgroundColor: alpha('#ecf0f1', 0.1),
                    }
                  }}
                >
                  {t('sidebar.history')}
                </Button>

                {shouldShowAddCreditsButton() && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setAddCreditsModalOpen(true)}
                    startIcon={<AddCardIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#000' : undefined,
                      backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 0.8) : alpha('#3498db', 0.8),
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? alpha('#65f0c5', 1) : alpha('#3498db', 1),
                      }
                    }}
                  >
                    {t('sidebar.addCredits')}
                  </Button>
                )}
              </Box>
            </Box>
          </Fade>
        </Box>
      )}

      {/* Footer Actions */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.15)', position: 'relative', zIndex: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={isExpanded ? <DashboardIcon /> : undefined}
          onClick={onNavigateToNormalDashboard}
          sx={{
            mb: 2,
            color: theme.palette.mode === 'dark' ? '#F8FAFC' : '#ecf0f1',
            borderColor: isExpanded ? alpha('#ecf0f1', 0.4) : 'transparent',
            backgroundColor: isExpanded ? 'transparent' : 'transparent',
            borderRadius: 2,
            textTransform: 'none',
            py: 1.5,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'flex-start' : 'center',
            position: 'relative',
            '& .MuiTouchRipple-root': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: isExpanded ? 'flex-start' : 'center',
            },
            '&:hover': {
              borderColor: isExpanded ? '#ecf0f1' : 'transparent',
              backgroundColor: isExpanded ? alpha('#ecf0f1', 0.1) : alpha('#ecf0f1', 0.05),
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {!isExpanded && <DashboardIcon sx={{ fontSize: '1.25rem' }} />}
          <Fade in={isExpanded} timeout={150}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              opacity: isExpanded ? 1 : 0,
              transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
            }}>
              {t('sidebar.leadManagement')}
            </Box>
          </Fade>
        </Button>

      </Box>

      {/* Credits Modal */}
      <Dialog
        open={creditsModalOpen}
        onClose={handleCreditsModalClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'text.primary',
          py: 3
        }}>
          {t('sidebar.continueUsing')}
        </DialogTitle>
        <DialogActions sx={{
          justifyContent: 'center',
          gap: 2,
          pb: 3,
          px: 3
        }}>
          <Button
            onClick={handleNavigateToCredits}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
            }}
          >
            {t('sidebar.subscribePlans')}
          </Button>
          <Button
            onClick={handleCreditsModalClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
            }}
          >
            {t('sidebar.dontSubscribeAndBack')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Adicionar Créditos */}
      {!shouldHidePaymentElements && (
        <AddCreditsModal
          open={addCreditsModalOpen}
          onClose={() => setAddCreditsModalOpen(false)}
          onSuccess={handleAddCreditsSuccess}
        />
      )}

    </Box>
  );
};

export default CollapsibleSidebar;